import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import GuideCard from "./components/GuideCard";
import GuidesCrossSell from "./components/GuidesCrossSell";
import { categories, guides } from "./guideData";

const stats = [
  { value: "11", label: "Free Guides", icon: "ri-book-open-line" },
  { value: "4", label: "Product Categories", icon: "ri-apps-line" },
  { value: "DIY", label: "Friendly Format", icon: "ri-tools-line" },
  { value: "Free", label: "Expert Support", icon: "ri-customer-service-2-line" },
];

const trustBadges = [
  { icon: "ri-shield-check-line", label: "Authorized Dealer" },
  { icon: "ri-truck-line", label: "Ships from US" },
  { icon: "ri-phone-line", label: "(866) 605-4975" },
  { icon: "ri-map-pin-line", label: "Used Nationwide" },
  { icon: "ri-award-line", label: "Manufacturer Warranty" },
];

const quizUseCase = [
  { icon: "ri-home-2-line", label: "Home Backup", desc: "Keep essentials running" },
  { icon: "ri-sun-line", label: "Off-Grid Cabin", desc: "Solar independence" },
  { icon: "ri-flashlight-line", label: "Emergency Prep", desc: "Ready when it matters" },
  { icon: "ri-store-line", label: "Shed / Office", desc: "Remote power needs" },
];

export default function InstallationGuidesPage() {
  const [activeCategory, setActiveCategory] = useState("all");

  const filtered =
    activeCategory === "all"
      ? guides
      : guides.filter((g) => g.categoryId === activeCategory);

  const beginnerCount = guides.filter((g) => g.difficulty === "Beginner").length;
  const intermediateCount = guides.filter((g) => g.difficulty === "Intermediate").length;

  return (
    <main className="font-sans bg-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <div className="pt-20 bg-gray-900">
        <div className="max-w-5xl mx-auto px-4 md:px-6 pt-10 pb-14">
          <nav className="flex items-center gap-1.5 text-xs text-gray-500 mb-6">
            <Link to="/" className="hover:text-amber-400 transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-600"></i>
            <span className="text-gray-300 font-medium">Installation Guides</span>
          </nav>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500/20 rounded-lg">
                  <i className="ri-tools-line text-lg text-amber-400"></i>
                </div>
                <span className="text-amber-400 text-xs font-bold uppercase tracking-widest">Free Resource</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-white leading-tight mb-3">
                Installation &amp; Setup Guides
              </h1>
              <p className="text-gray-400 text-base leading-relaxed">
                Step-by-step instructions for every Iron Haven product type — from first-time portable station setup to whole-home standby pre-installation. No fluff, just clear steps.
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3 md:w-64 shrink-0">
              {stats.map((s) => (
                <div key={s.label} className="bg-gray-800 rounded-xl px-4 py-3 text-center">
                  <div className="w-6 h-6 flex items-center justify-center mx-auto mb-1">
                    <i className={`${s.icon} text-base text-amber-400`}></i>
                  </div>
                  <p className="text-white font-extrabold text-lg leading-none">{s.value}</p>
                  <p className="text-gray-400 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Difficulty legend */}
          <div className="mt-8 flex flex-wrap items-center gap-4 pt-6 border-t border-gray-800">
            <span className="text-gray-500 text-xs font-medium">Difficulty levels:</span>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
              <span className="text-gray-400 text-xs">{beginnerCount} Beginner</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500 inline-block"></span>
              <span className="text-gray-400 text-xs">{intermediateCount} Intermediate</span>
            </div>
            <div className="ml-auto hidden sm:flex items-center gap-1.5 text-gray-500 text-xs">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-alert-line text-xs text-amber-400"></i>
              </div>
              Intermediate+ guides recommend a licensed professional
            </div>
          </div>
        </div>
      </div>

      {/* ── Find Your System Banner ── */}
      <div className="bg-amber-500">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-5">
            <div className="text-center md:text-left">
              <p className="text-white font-extrabold text-lg leading-tight mb-1">
                Not sure which system you need?
              </p>
              <p className="text-amber-100 text-sm">
                Answer 3 quick questions — we&apos;ll match you to the right product in under 60 seconds.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <div className="flex items-center gap-2 flex-wrap justify-center">
                {quizUseCase.map((uc) => (
                  <div key={uc.label} className="flex items-center gap-1.5 bg-amber-600/50 rounded-lg px-2.5 py-1.5">
                    <div className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className={`${uc.icon} text-white text-xs`}></i>
                    </div>
                    <span className="text-white text-xs font-semibold whitespace-nowrap">{uc.label}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/#system-quiz-section"
                className="flex items-center gap-2 bg-white hover:bg-amber-50 text-amber-700 font-bold text-sm px-6 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap shrink-0"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-flashlight-fill text-amber-500"></i>
                </div>
                Find My System
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line"></i>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Safety callout */}
      <div className="bg-amber-50 border-b border-amber-200">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3.5 flex items-start gap-3">
          <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-shield-check-line text-base text-amber-600"></i>
          </div>
          <p className="text-amber-800 text-xs leading-relaxed">
            <strong>Safety first:</strong> Guides marked <em>Intermediate</em> or higher involve electrical wiring or fuel systems and should be performed by or alongside a licensed electrician or gas technician. Always follow local codes and manufacturer instructions.
          </p>
        </div>
      </div>

      {/* Trust badges strip */}
      <div className="border-b border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center gap-6 flex-wrap">
          {trustBadges.map((b) => (
            <div key={b.label} className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${b.icon} text-amber-500 text-sm`}></i>
              </div>
              <span className="text-gray-600 text-xs font-semibold whitespace-nowrap">{b.label}</span>
            </div>
          ))}
          <div className="ml-auto hidden md:flex items-center gap-1.5">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-store-2-line text-amber-500 text-sm"></i>
            </div>
            <Link to="/shop" className="text-amber-600 hover:text-amber-500 text-xs font-bold transition-colors cursor-pointer whitespace-nowrap">
              Shop All Products &rarr;
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-10">

        {/* Category filter tabs */}
        <div className="flex items-center gap-2 flex-wrap mb-8">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === cat.id
                  ? "bg-gray-900 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
              }`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className={`${cat.icon} text-sm`}></i>
              </span>
              {cat.label}
              <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${
                activeCategory === cat.id ? "bg-white/20 text-white" : "bg-gray-200 text-gray-500"
              }`}>
                {cat.id === "all" ? guides.length : guides.filter((g) => g.categoryId === cat.id).length}
              </span>
            </button>
          ))}
        </div>

        {/* Guide grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((guide) => (
              <GuideCard key={guide.id} guide={guide} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 text-gray-400 text-sm">No guides in this category yet.</div>
        )}

        {/* Mid-page CTA — shop by use case */}
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: "ri-home-2-line", label: "Home Backup", sub: "Keep your essentials running", link: "/shop?category=solar&price=500to1000", color: "bg-amber-50 border-amber-200 hover:border-amber-400" },
            { icon: "ri-sun-line", label: "Off-Grid Solar", sub: "Power anywhere, no grid needed", link: "/shop?category=solar&price=1000to2500", color: "bg-amber-50 border-amber-200 hover:border-amber-400" },
            { icon: "ri-gas-station-line", label: "Gas Generators", sub: "High-output portable power", link: "/shop?category=gas", color: "bg-gray-50 border-gray-200 hover:border-gray-400" },
            { icon: "ri-community-line", label: "Whole-Home Standby", sub: "Automatic backup, zero effort", link: "/shop?category=whole-home", color: "bg-gray-50 border-gray-200 hover:border-gray-400" },
          ].map((item) => (
            <Link
              key={item.label}
              to={item.link}
              className={`flex flex-col items-center text-center p-5 rounded-xl border transition-all cursor-pointer group ${item.color}`}
            >
              <div className="w-10 h-10 flex items-center justify-center bg-white rounded-xl border border-gray-200 mb-3 group-hover:border-amber-300 transition-colors">
                <i className={`${item.icon} text-xl text-amber-500`}></i>
              </div>
              <p className="text-sm font-bold text-gray-900 mb-1">{item.label}</p>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">{item.sub}</p>
              <span className="text-xs font-bold text-amber-600 flex items-center gap-1">
                Shop Now
                <i className="ri-arrow-right-line text-xs"></i>
              </span>
            </Link>
          ))}
        </div>

        {/* Cross-sell: Recently Viewed + Customers Also Bought */}
        <GuidesCrossSell />

        {/* Bottom CTA */}
        <div className="mt-10 bg-gray-900 rounded-2xl overflow-hidden">
          <div className="px-6 md:px-10 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <p className="text-white font-extrabold text-xl mb-2">Need hands-on help?</p>
              <p className="text-gray-400 text-sm leading-relaxed max-w-md">
                Our support team can walk you through setup, recommend a local installer, or help troubleshoot your system. We respond within 1 business day.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
              <Link
                to="/faq"
                className="flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-questionnaire-line text-base text-amber-400"></i>
                </span>
                Browse FAQ
              </Link>
              <Link
                to="/contact"
                className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold px-5 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-customer-service-2-line text-base"></i>
                </span>
                Contact Support
              </Link>
            </div>
          </div>

          {/* Contact strip */}
          <div className="border-t border-gray-800 px-6 md:px-10 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-gray-500 text-xs">Mon–Fri, 9am–5pm EST</p>
            <div className="flex items-center gap-5">
              <a href="tel:8666054975" className="flex items-center gap-1.5 text-gray-400 hover:text-amber-400 text-xs transition-colors cursor-pointer">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-phone-line text-xs"></i>
                </span>
                (866) 605-4975
              </a>
              <a href="mailto:support@ironhavensupply.com" className="flex items-center gap-1.5 text-gray-400 hover:text-amber-400 text-xs transition-colors cursor-pointer">
                <span className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-mail-line text-xs"></i>
                </span>
                support@ironhavensupply.com
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
